import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Schedule as DurationIcon,
  Star as StarIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import type { StrategyAttempt } from '../../../services/behaviorService';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface StrategyEffectivenessCardProps {
  strategies: StrategyAttempt[];
}

interface StrategyStats {
  name: string;
  totalAttempts: number;
  avgRating: number;
  totalDays: number;
  tier: number;
}

// ═══════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════

function calculateDurationDays(startedAt: string, endedAt?: string): number {
  const start = new Date(startedAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diffTime = end.getTime() - start.getTime();
  return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
}

function getRatingColor(rating: number): 'error' | 'warning' | 'success' | 'info' {
  if (rating >= 4) return 'success';
  if (rating >= 3) return 'info';
  if (rating >= 2) return 'warning';
  return 'error';
}

// ═══════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════

const StrategyEffectivenessCard: React.FC<StrategyEffectivenessCardProps> = ({
  strategies,
}) => {
  // Process strategies data
  const strategyStats = useMemo(() => {
    const statsMap: Record<string, StrategyStats> = {};
    
    strategies.forEach((s) => {
      const name = s.strategy.name;
      if (!statsMap[name]) {
        statsMap[name] = {
          name,
          totalAttempts: 0,
          avgRating: 0,
          totalDays: 0,
          tier: s.strategy.tier || 1,
        };
      }
      
      statsMap[name].totalAttempts += 1;
      if (s.outcomeRating) {
        const current = statsMap[name].avgRating;
        const count = statsMap[name].totalAttempts;
        statsMap[name].avgRating = (current * (count - 1) + s.outcomeRating) / count;
      }
      statsMap[name].totalDays += calculateDurationDays(s.startedAt, s.endedAt);
    });
    
    return Object.values(statsMap).sort((a, b) => b.avgRating - a.avgRating);
  }, [strategies]);

  const activeStrategies = strategies.filter((s) => s.status === 'active').length;
  const topStrategy = strategyStats[0];
  
  // Calculate overall effectiveness
  const overallRating = useMemo(() => {
    if (strategyStats.length === 0) return 0;
    return strategyStats.reduce((sum, s) => sum + s.avgRating, 0) / strategyStats.length;
  }, [strategyStats]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Strategy Effectiveness
        </Typography>
        
        {/* Overall Score */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 3,
            p: 2,
            backgroundColor: overallRating >= 3.5 ? 'success.light' : 
                            overallRating >= 2.5 ? 'info.light' : 'warning.light',
            borderRadius: 2,
          }}
        >
          <TrophyIcon 
            sx={{ 
              fontSize: 40, 
              color: overallRating >= 3.5 ? 'success.main' : 
                     overallRating >= 2.5 ? 'info.main' : 'warning.main'
            }} 
          />
          <Box>
            <Typography variant="h4">
              {overallRating.toFixed(1)}
              <Typography component="span" variant="body2" color="text.secondary">
                /5
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall Effectiveness Score
            </Typography>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="h5">{activeStrategies}</Typography>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="h5">{strategyStats.length}</Typography>
            <Typography variant="caption" color="text.secondary">
              Tried
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="h5">
              {topStrategy ? topStrategy.name : '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Best Strategy
            </Typography>
          </Box>
        </Box>

        {/* Strategy List */}
        <Typography variant="subtitle2" gutterBottom>
          Strategy Performance
        </Typography>
        
        {strategyStats.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
            No strategies recorded yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {strategyStats.slice(0, 5).map((strategy) => (
              <Box key={strategy.name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {strategy.name}
                    </Typography>
                    <Chip 
                      label={`T${strategy.tier}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ height: 20, fontSize: 10 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon 
                      sx={{ 
                        fontSize: 16, 
                        color: getRatingColor(strategy.avgRating) + '.main'
                      }} 
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {strategy.avgRating.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
                
                <LinearProgress
                  variant="determinate"
                  value={(strategy.avgRating / 5) * 100}
                  color={getRatingColor(strategy.avgRating)}
                  sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                />
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {strategy.totalAttempts} attempt{strategy.totalAttempts !== 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {strategy.totalDays} day{strategy.totalDays !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategyEffectivenessCard;
